// Lecteur web des animations d'entrée (calques SVG et items d'interface d'une scène SVG inline).
//
// Le mouvement est précalculé en images clés par le module pur `scene_animation_entree_settings.js`
// puis confié au navigateur par l'API Web Animations : aucune boucle JavaScript par image, aucun
// requestAnimationFrame ; le navigateur cadence lui-même l'animation et l'arrête seul.
// L'animation est posée sur la propriété CSS `transform` de l'élément, qui prend le pas sur son
// attribut `transform` le temps de l'animation (remplissage « backwards » seulement) : à la fin,
// l'effet disparaît et l'attribut, intact, reprend la main. Aucun nœud n'est ajouté ni déplacé,
// aucun `will-change` n'est posé (leçon du flash de décor : jamais de calque compositeur forcé dans
// l'illustration).
//
// Module sans Meteor : il ne dépend que du DOM, pour être repris tel quel par diffusion.

import {
  animation_entree_active,
  images_cles_animation_entree,
  multiplie_matrices_animation_entree,
  normalise_animation_entree,
} from "../../imports/scene_animation_entree_settings.js";

const CLE_ETAT = "__cogiAnimationEntree";

function matrice_depuis_dom (pM)
  {
    if (!pM) return null;
    return {a : pM.a, b : pM.b, c : pM.c, d : pM.d, e : pM.e, f : pM.f};
  }

function inverse_matrice (m)
  {
    const tDet = (m.a * m.d) - (m.b * m.c);
    if (!tDet || !Number.isFinite (tDet)) return null;
    return {
      a : m.d / tDet,
      b : -m.b / tDet,
      c : -m.c / tDet,
      d : m.a / tDet,
      e : ((m.c * m.f) - (m.d * m.e)) / tDet,
      f : ((m.b * m.e) - (m.a * m.f)) / tDet,
    };
  }

function applique_point (m, x, y)
  {
    return {x : (m.a * x) + (m.c * y) + m.e, y : (m.b * x) + (m.d * y) + m.f};
  }

function nombre_css (pValeur)
  {
    const tV = Math.round ((Number (pValeur) || 0) * 1e6) / 1e6;
    return Object.is (tV, -0) ? "0" : String (tV);
  }

function matrice_css (m)
  {
    return `matrix(${nombre_css (m.a)}, ${nombre_css (m.b)}, ${nombre_css (m.c)}, ${nombre_css (m.d)}, ${nombre_css (m.e)}, ${nombre_css (m.f)})`;
  }

// Matrice « repère local de l'élément → repère utilisateur de la racine SVG ».
// Passer par getScreenCTM des deux côtés neutralise le viewBox (caméra) et les transformations
// CSS de la page : seule compte la géométrie de la scène.
function matrice_vers_racine (pNoeud, pInverseRacine)
  {
    if (!pNoeud || typeof pNoeud.getScreenCTM !== "function") return null;
    let tEcran = null;
    try { tEcran = pNoeud.getScreenCTM (); }
    catch (e) { tEcran = null; }
    const tM = matrice_depuis_dom (tEcran);
    return tM ? multiplie_matrices_animation_entree (pInverseRacine, tM) : null;
  }

function bbox_dans_racine (pNoeud, pMatrice)
  {
    let tBBox = null;
    try { tBBox = pNoeud.getBBox (); }
    catch (e) { tBBox = null; }
    if (!tBBox) return null;
    const tCoins = [
      applique_point (pMatrice, tBBox.x, tBBox.y),
      applique_point (pMatrice, tBBox.x + tBBox.width, tBBox.y),
      applique_point (pMatrice, tBBox.x, tBBox.y + tBBox.height),
      applique_point (pMatrice, tBBox.x + tBBox.width, tBBox.y + tBBox.height),
    ];
    const tXs = tCoins.map ((p) => p.x);
    const tYs = tCoins.map ((p) => p.y);
    const x = Math.min (...tXs);
    const y = Math.min (...tYs);
    return {x, y, w : Math.max (...tXs) - x, h : Math.max (...tYs) - y};
  }

function restaure_styles (pNoeud)
  {
    const tEtat = pNoeud && pNoeud[CLE_ETAT];
    if (!tEtat) return;
    pNoeud.style.transformOrigin = tEtat.transformOrigin;
    pNoeud.style.transformBox = tEtat.transformBox;
    pNoeud[CLE_ETAT] = null;
  }

export function annule_animation_entree_element (pNoeud)
  {
    const tEtat = pNoeud && pNoeud[CLE_ETAT];
    if (!tEtat) return;
    try { tEtat.animation.cancel (); }
    catch (e) {}
    restaure_styles (pNoeud);
  }

// Mesure un élément et prépare ses images clés, sans rien démarrer.
// pOptions : {racine : <svg> racine de la scène, vue : {x, y, w, h} vue de la scène (repère racine),
//             sansDelai : bool (aperçu de l'inspecteur), delaiSupplementaireMs : nombre}
function prepare_animation_entree_element (pNoeud, pReglages, pOptions = {})
  {
    if (!pNoeud || typeof pNoeud.animate !== "function") return null;
    const tReglages = normalise_animation_entree (pReglages);
    if (!animation_entree_active (tReglages)) return null;

    const tRacine = pOptions.racine || pNoeud.ownerSVGElement;
    let tEcranRacine = null;
    try { tEcranRacine = tRacine && tRacine.getScreenCTM (); }
    catch (e) { tEcranRacine = null; }
    const tInverseRacine = tEcranRacine ? inverse_matrice (matrice_depuis_dom (tEcranRacine)) : null;
    if (!tInverseRacine) return null;
    const tC = matrice_vers_racine (pNoeud, tInverseRacine);
    const tParent = pNoeud.parentNode;
    const tP = tParent === tRacine
      ? {a : 1, b : 0, c : 0, d : 1, e : 0, f : 0}
      : matrice_vers_racine (tParent, tInverseRacine);
    const tPi = tP ? inverse_matrice (tP) : null;
    if (!tC || !tPi) return null;
    const tBBox = bbox_dans_racine (pNoeud, tC);
    if (!tBBox) return null;

    let tOpacite = 1;
    try { tOpacite = Number (getComputedStyle (pNoeud).opacity); }
    catch (e) { tOpacite = 1; }
    if (!Number.isFinite (tOpacite)) tOpacite = 1;

    const tCles = images_cles_animation_entree (tReglages, {bbox : tBBox, vue : pOptions.vue || null, opacite : tOpacite});
    if (!tCles.duree_ms) return null;
    const tAnimeOpacite = tReglages.type === "fondu" || tReglages.fondu_simultane;
    // Transformation CSS = P⁻¹ · A · C : l'animation A, exprimée dans le repère de la scène,
    // s'ajoute par-dessus la géométrie normale de l'élément.
    const tImages = tCles.images.map ((pImage) =>
      {
        const tT = multiplie_matrices_animation_entree (tPi, multiplie_matrices_animation_entree (pImage.matrice, tC));
        const tImage = {offset : pImage.offset, transform : matrice_css (tT)};
        if (tAnimeOpacite) tImage.opacity = nombre_css (pImage.opacite);
        return tImage;
      });
    return {
      noeud : pNoeud,
      images : tImages,
      duree_ms : tCles.duree_ms,
      delai_ms : (pOptions.sansDelai ? 0 : tReglages.delai_ms) + Math.max (0, Number (pOptions.delaiSupplementaireMs) || 0),
    };
  }

function demarre_animation_entree_preparee (pPlan)
  {
    const tNoeud = pPlan.noeud;
    tNoeud[CLE_ETAT] = {
      transformOrigin : tNoeud.style.transformOrigin,
      transformBox : tNoeud.style.transformBox,
      animation : null,
    };
    // Origine et boîte de référence d'une transformation SVG classique : la matrice CSS a alors
    // exactement le sens de l'attribut transform.
    tNoeud.style.transformOrigin = "0px 0px";
    tNoeud.style.transformBox = "view-box";
    let tAnimation = null;
    try
      {
        tAnimation = tNoeud.animate (pPlan.images, {
          duration : pPlan.duree_ms,
          delay : pPlan.delai_ms,
          easing : "linear",
          fill : "backwards",
        });
      }
    catch (e)
      {
        restaure_styles (tNoeud);
        return null;
      }
    tNoeud[CLE_ETAT].animation = tAnimation;
    const tFin = () =>
      {
        if (tNoeud[CLE_ETAT] && tNoeud[CLE_ETAT].animation === tAnimation)
          restaure_styles (tNoeud);
      };
    tAnimation.addEventListener ("finish", tFin);
    tAnimation.addEventListener ("cancel", tFin);
    return tAnimation;
  }

// Joue l'animation d'entrée d'un seul élément (aperçu de l'inspecteur).
export function joue_animation_entree_element (pNoeud, pReglages, pOptions = {})
  {
    annule_animation_entree_element (pNoeud);
    const tPlan = prepare_animation_entree_element (pNoeud, pReglages, pOptions);
    return tPlan ? demarre_animation_entree_preparee (tPlan) : null;
  }

// Joue toutes les animations d'entrée d'une scène.
// pEntrees : [{id, animation_entree}] (entrées d'objets_config) ; pOptions : voir ci-dessus + {ids}.
export function joue_animations_entree_scene (pDoc, pEntrees, pOptions = {})
  {
    const tRacine = pOptions.racine || (pDoc && pDoc.documentElement);
    if (!pDoc || !tRacine || !Array.isArray (pEntrees)) return 0;
    const tFiltre = Array.isArray (pOptions.ids) ? new Set (pOptions.ids) : null;
    // Toutes les mesures d'abord, toutes les animations ensuite : une seule mise en page, et un
    // départ commun pour que les délais restent exacts entre éléments.
    const tAJouer = [];
    for (const tEntree of pEntrees)
      {
        if (!tEntree || !tEntree.id || !animation_entree_active (tEntree.animation_entree)) continue;
        if (tFiltre && !tFiltre.has (tEntree.id)) continue;
        const tNoeud = pDoc.getElementById (tEntree.id);
        if (!tNoeud) continue;
        annule_animation_entree_element (tNoeud);
        tAJouer.push ({noeud : tNoeud, reglages : tEntree.animation_entree});
      }
    const tPlans = tAJouer
      .map ((pItem) => prepare_animation_entree_element (pItem.noeud, pItem.reglages, {...pOptions, racine : tRacine}))
      .filter (Boolean);
    const tAnimations = tPlans.map ((pPlan) => demarre_animation_entree_preparee (pPlan)).filter (Boolean);
    // En attente (scène encore masquée, par exemple par l'écran d'entrée de diffusion) : chaque
    // élément est tenu à son état de départ jusqu'à reprend_animations_entree_scene.
    if (pOptions.enAttente)
      tAnimations.forEach ((pAnimation) => { try { pAnimation.pause (); } catch (e) {} });
    return tAnimations.length;
  }

function animations_entree_scene (pDoc)
  {
    const tRacine = pDoc && pDoc.documentElement;
    if (!tRacine || typeof tRacine.getAnimations !== "function") return [];
    try
      {
        return tRacine.getAnimations ({subtree : true}).filter ((pAnimation) =>
          {
            const tCible = pAnimation.effect && pAnimation.effect.target;
            return !!(tCible && tCible[CLE_ETAT] && tCible[CLE_ETAT].animation === pAnimation);
          });
      }
    catch (e) { return []; }
  }

// Lance depuis leur début les animations d'entrée jouées « en attente ».
export function reprend_animations_entree_scene (pDoc)
  {
    let tNombre = 0;
    animations_entree_scene (pDoc).forEach ((pAnimation) =>
      {
        if (pAnimation.playState !== "paused") return;
        try
          {
            pAnimation.currentTime = 0;
            pAnimation.play ();
            tNombre++;
          }
        catch (e) {}
      });
    return tNombre;
  }

export function annule_animations_entree_scene (pDoc)
  {
    animations_entree_scene (pDoc).forEach ((pAnimation) =>
      annule_animation_entree_element (pAnimation.effect.target));
  }
